/**
 * PURPOSE: Confirmación pequeña reutilizable del módulo de Atletismo (MUI Joy).
 * RESPONSIBILITIES: `useConfirmDialog()` devuelve `confirm(opciones) → Promise<boolean>` y el
 *   elemento del diálogo para montarlo. Se usa para descartar cambios, armar la final y quitar un
 *   inscrito de una serie.
 * COLLABORATORS: AtletismoResultadosArea.jsx, PruebaDetailPanel.jsx, RoundEditor.jsx.
 * ROUTE: winscore/src/area/AtletismoResultadosArea/components/ConfirmDialog.jsx
 */
import React, { useCallback, useRef, useState } from "react";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import ModalClose from "@mui/joy/ModalClose";
import DialogTitle from "@mui/joy/DialogTitle";
import DialogContent from "@mui/joy/DialogContent";
import Button from "@mui/joy/Button";

export const ConfirmDialog = ({
  open,
  title,
  message,
  confirmLabel = "Confirmar",
  cancelLabel = "Cancelar",
  tone = "primary",
  onConfirm,
  onCancel,
}) => (
  <Modal open={Boolean(open)} onClose={onCancel}>
    <ModalDialog sx={{ width: "min(92vw, 460px)", borderRadius: "16px" }}>
      <ModalClose />
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {typeof message === "string" ? (
          <p className="text-sm leading-6 text-slate-600">{message}</p>
        ) : (
          message
        )}
      </DialogContent>
      <div className="mt-3 flex justify-end gap-2">
        <Button variant="plain" color="neutral" onClick={onCancel}>
          {cancelLabel}
        </Button>
        <Button variant="solid" color={tone === "danger" ? "danger" : "primary"} onClick={onConfirm} autoFocus>
          {confirmLabel}
        </Button>
      </div>
    </ModalDialog>
  </Modal>
);

export const useConfirmDialog = () => {
  const [options, setOptions] = useState(null);
  const resolverRef = useRef(null);

  const settle = useCallback((result) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setOptions(null);
    if (resolve) resolve(result);
  }, []);

  const confirm = useCallback(
    (nextOptions) =>
      new Promise((resolve) => {
        if (resolverRef.current) resolverRef.current(false);
        resolverRef.current = resolve;
        setOptions(nextOptions || {});
      }),
    []
  );

  const dialog = (
    <ConfirmDialog
      open={Boolean(options)}
      {...(options || {})}
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
    />
  );

  return { confirm, dialog };
};

/** Opciones estándar para "hay cambios sin guardar". */
export const DISCARD_CHANGES_OPTIONS = Object.freeze({
  title: "Cambios sin guardar",
  message: "Hay marcas sin guardar en esta serie. Si continúas se perderán.",
  confirmLabel: "Descartar cambios",
  cancelLabel: "Seguir editando",
  tone: "danger",
});
